import React, { useEffect, useState } from "react";
import TextFieldGroup from "../../commons/TextFieldGroup";
import Searchbar from "../../commons/Searchbar";

import {
  Layout,
  Breadcrumb,
  Form,
  Table,
  Divider,
  Row,
  Col,
  Collapse,
  PageHeader,
  Button,
  Input,
  message,
} from "antd";

import {
  formItemLayout,
  smallFormItemLayout,
  tailFormItemLayout,
} from "./../../utils/Layouts";
import { EditOutlined, CloseOutlined, SearchOutlined } from "@ant-design/icons";
import isEmpty from "../../validation/is-empty";
import { useSelector } from "react-redux";
import {
  edit,
  onDelete,
  onSubmit,
  onSearch,
  onChange,
} from "../../utils/form_utilities";
import moment from "moment";
import DatePickerFieldGroup from "../../commons/DatePickerFieldGroup";
import numberFormat from "../../utils/numberFormat";
import { authenticateAdmin } from "../../utils/authentications";
import {
  onBranchSearch,
  onCustomerSearch,
  onProjectTypeSearch,
} from "../../utils/utilities";
import SelectFieldGroup from "../../commons/SelectFieldGroup";
import RangeDatePickerFieldGroup from "../../commons/RangeDatePickerFieldGroup";
import axios from "axios";
import {
  account_type_options,
  customer_pricing_options,
} from "../../utils/Options";
import SimpleSelectFieldGroup from "../../commons/SimpleSelectFieldGroup";
import {
  ACCOUNT_TYPE_CONSIGNEE,
  ACCOUNT_TYPE_CUSTOMER,
} from "../../utils/constants";
import CheckboxFieldGroup from "../../commons/CheckboxFieldGroup";
import round from "../../utils/round";

const { Content } = Layout;
const { Panel } = Collapse;

const url = "/api/projects/";
const title = "Project Form";

const initialValues = {
  _id: null,
  name: "",
  address: "",
  owner: "",
  contact_no: "",
  terms: "",
  opening_balance: 0,
  opening_balance_date: moment(),
  terms_in_days: null,
  is_taxable: true,
  is_vat_exclusive: true,
};
const date_fields = ["date_awarded", "date_of_completion", "due_date"];

export default function ProjectForm({ history }) {
  const [errors, setErrors] = useState({});
  const [records, setRecords] = useState([]);
  const [total_records, setTotalRecords] = useState(null);
  const [current_page, setCurrentPage] = useState(1);
  const [search_keyword, setSearchKeyword] = useState("");
  const auth = useSelector((state) => state.auth);
  const [options, setOptions] = useState({});
  const [search_state, setSearchState] = useState({
    branch: null,
  });

  const [state, setState] = useState(initialValues);

  const records_column = [
    {
      title: "Date Awarded",
      dataIndex: "date_awarded",
      render: (date) => date && moment(date).format("MM/DD/YYYY"),
    },
    {
      title: "Company",
      dataIndex: ["account", "name"],
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Project Type",
      dataIndex: "project_type",
    },
    {
      title: "Project Price",
      dataIndex: "project_price",
      align: "right",
      render: (value) => numberFormat(value),
    },
    {
      title: "Vat Amount",
      dataIndex: "vat_amount",
      align: "right",
      render: (value) => numberFormat(value),
    },
    {
      title: "Total Gross Amount",
      dataIndex: "total_gross_amount",
      align: "right",
      render: (value) => numberFormat(value),
    },
    {
      title: "Total Billing",
      dataIndex: "total_billing_amount",
      align: "right",
      render: (value) => numberFormat(value),
    },
    {
      title: "Date of Completion",
      dataIndex: "date_of_completion",
      align: "center",
      render: (date) => date && moment(date).format("MM/DD/YYYY"),
    },
  ];

  useEffect(() => {
    authenticateAdmin({
      role: auth.user?.role,
      history,
    });
    return () => {};
  }, []);

  useEffect(() => {
    let vat_amount = 0,
      total_gross_amount = 0;
    const project_price = parseFloat(state.project_price);

    if (state.is_taxable) {
      if (state.is_vat_exclusive) {
        vat_amount = round(project_price * 0.12);
        total_gross_amount = round(project_price + vat_amount);
      } else {
        vat_amount = round((project_price / 1.12) * 0.12);
        total_gross_amount = project_price;
      }
    } else {
      total_gross_amount = project_price;
      vat_amount = 0;
    }

    setState((prevState) => ({
      ...prevState,
      vat_amount,
      total_gross_amount,
    }));

    return () => {};
  }, [state.is_taxable, state.is_vat_exclusive, state.project_price]);

  return (
    <Content className="content-padding">
      <div className="columns is-marginless">
        <div className="column">
          <Breadcrumb style={{ margin: "16px 0" }}>
            <Breadcrumb.Item>Home</Breadcrumb.Item>
            <Breadcrumb.Item className="text-color-yellow">
              {title}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className="column">
          <Searchbar
            name="search_keyword"
            onSearch={(value, e) => {
              e.preventDefault();
              onSearch({
                page: 1,
                search_keyword,
                url,
                setRecords,
                setTotalRecords,
                setCurrentPage,
                setErrors,
                advance_search: search_state,
              });
            }}
            onChange={(e) => setSearchKeyword(e.target.value)}
            value={search_keyword}
            onNew={() => {
              setRecords([]);
              setState(initialValues);
            }}
          />
        </div>
      </div>

      {/* Start of Advance Search */}
      <Row>
        <Col span={24} className="m-b-1">
          <Collapse>
            <Panel header="Advance Search">
              <PageHeader
                backIcon={false}
                style={{
                  border: "1px solid rgb(235, 237, 240)",
                }}
                onBack={() => null}
                title="Advance Filter"
                subTitle="Enter appropriate data to filter records"
              >
                <div className="or-slip-form">
                  <Row>
                    <Col span={8}>
                      <SelectFieldGroup
                        label="Company"
                        value={state.account?.name}
                        onSearch={(value) =>
                          onCustomerSearch({ value, options, setOptions })
                        }
                        onChange={(index) => {
                          const account = options.accounts[index];
                          setSearchState((prevState) => ({
                            ...prevState,
                            account,
                          }));
                        }}
                        error={errors.account}
                        formItemLayout={smallFormItemLayout}
                        data={options.accounts}
                        column="name"
                      />
                    </Col>
                    <Col span={8}>
                      <TextFieldGroup
                        label="Project"
                        name="name"
                        error={errors.name}
                        formItemLayout={smallFormItemLayout}
                        value={search_state.name}
                        onChange={(e) => {
                          onChange({
                            key: e.target.name,
                            value: e.target.value,
                            setState: setSearchState,
                          });
                        }}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col span={8}>
                      <Row>
                        <Col offset={8} span={12}>
                          <Button
                            type="info"
                            size="small"
                            icon={<SearchOutlined />}
                            onClick={() => {
                              onSearch({
                                page: 1,
                                search_keyword,
                                url,
                                setRecords,
                                setTotalRecords,
                                setCurrentPage,
                                setErrors,
                                advance_search: search_state,
                              });
                            }}
                          >
                            Search
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                    <Col span={8}></Col>
                    <Col span={8}></Col>
                  </Row>
                </div>
              </PageHeader>
            </Panel>
          </Collapse>
        </Col>
      </Row>
      {/* End of Advanced Search */}

      <div
        style={{
          background: "#fff",
          padding: 24,
          minHeight: 280,
          borderBottomLeftRadius: 10,
          borderBottomRightRadius: 10,
        }}
      >
        <span className="module-title">{title}</span>
        <Divider />
        {isEmpty(records) ? (
          <Form initialValues={initialValues}>
            <SelectFieldGroup
              label="Company"
              value={state.account?.name}
              onSearch={(value) =>
                onCustomerSearch({ value, options, setOptions })
              }
              onFocus={(value) =>
                onCustomerSearch({ value: "", options, setOptions })
              }
              onChange={(index) => {
                const account = options.accounts[index];
                setState((prevState) => ({
                  ...prevState,
                  account,
                }));
              }}
              error={errors.account}
              formItemLayout={formItemLayout}
              data={options.accounts}
              column="name"
            />

            <TextFieldGroup
              label="Project Title"
              name="name"
              error={errors.name}
              formItemLayout={formItemLayout}
              value={state.name}
              onChange={(e) => {
                onChange({
                  key: e.target.name,
                  value: e.target.value,
                  setState,
                });
              }}
            />

            <Row>
              <Col span={12}>
                <SelectFieldGroup
                  label="Project Type"
                  value={state.project_type}
                  onFocus={() => {
                    onProjectTypeSearch({ value: "", setOptions });
                  }}
                  onSearch={(value) =>
                    onProjectTypeSearch({ value, setOptions })
                  }
                  onChange={(index) => {
                    const project_type = options.project_types?.[index] || null;
                    setState((prevState) => ({
                      ...prevState,
                      project_type: project_type?.name || null,
                    }));
                  }}
                  formItemLayout={smallFormItemLayout}
                  data={options.project_types}
                  column="name"
                  error={errors.project_type}
                />
              </Col>
              <Col span={12}>
                <TextFieldGroup
                  type="number"
                  label="Project Price"
                  name="project_price"
                  error={errors.project_price}
                  formItemLayout={smallFormItemLayout}
                  value={state.project_price}
                  onChange={(e) => {
                    onChange({
                      key: e.target.name,
                      value: e.target.value,
                      setState,
                    });
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <CheckboxFieldGroup
                  label="Taxable"
                  name="is_taxable"
                  error={errors.is_taxable}
                  checked={state.is_taxable}
                  formItemLayout={smallFormItemLayout}
                  onChange={(e) => {
                    onChange({
                      key: e.target.name,
                      value: e.target.checked,
                      setState,
                    });
                  }}
                />
              </Col>
              <Col span={12}>
                <CheckboxFieldGroup
                  label="Vat Ex"
                  name="is_vat_exclusive"
                  error={errors.is_vat_exclusive}
                  checked={state.is_vat_exclusive}
                  formItemLayout={smallFormItemLayout}
                  onChange={(e) => {
                    onChange({
                      key: e.target.name,
                      value: e.target.checked,
                      setState,
                    });
                  }}
                />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <DatePickerFieldGroup
                  label="Date Awarded"
                  name="date_awarded"
                  value={state.date_awarded || null}
                  onChange={(value) => {
                    setState((prevState) => ({
                      ...prevState,
                      date_awarded: value,
                    }));
                  }}
                  error={errors.date_awarded}
                  formItemLayout={smallFormItemLayout}
                />
              </Col>
              <Col span={12}>
                <DatePickerFieldGroup
                  label="Date of Completion"
                  name="date_of_completion"
                  value={state.date_of_completion || null}
                  onChange={(value) => {
                    setState((prevState) => ({
                      ...prevState,
                      date_of_completion: value,
                    }));
                  }}
                  error={errors.date_of_completion}
                  formItemLayout={smallFormItemLayout}
                />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <DatePickerFieldGroup
                  label="Due Date"
                  name="due_date"
                  value={state.due_date || null}
                  onChange={(value) => {
                    setState((prevState) => ({
                      ...prevState,
                      due_date: value,
                    }));
                  }}
                  error={errors.due_date}
                  formItemLayout={smallFormItemLayout}
                />
              </Col>
            </Row>
            <Row>
              <Col span={12}>
                <TextFieldGroup
                  disabled
                  label="Vat Amount"
                  name="vat_amount"
                  error={errors.vat_amount}
                  formItemLayout={smallFormItemLayout}
                  value={state.vat_amount}
                  onChange={(e) => {
                    onChange({
                      key: e.target.name,
                      value: e.target.value,
                      setState,
                    });
                  }}
                />
              </Col>
              <Col span={12}>
                <TextFieldGroup
                  disabled
                  label="Gross Amount"
                  name="total_gross_amount"
                  error={errors.total_gross_amount}
                  formItemLayout={smallFormItemLayout}
                  value={state.total_gross_amount}
                  onChange={(e) => {
                    onChange({
                      key: e.target.name,
                      value: e.target.value,
                      setState,
                    });
                  }}
                />
              </Col>
            </Row>

            <TextFieldGroup
              label="Sales Person"
              name="sales_person"
              error={errors.sales_person}
              formItemLayout={formItemLayout}
              value={state.sales_person}
              onChange={(e) => {
                onChange({
                  key: e.target.name,
                  value: e.target.value,
                  setState,
                });
              }}
            />

            <TextFieldGroup
              label="Remarks"
              name="remarks"
              error={errors.remarks}
              formItemLayout={formItemLayout}
              value={state.remarks}
              onChange={(e) => {
                onChange({
                  key: e.target.name,
                  value: e.target.value,
                  setState,
                });
              }}
            />

            <Form.Item className="m-t-1" {...tailFormItemLayout}>
              <div className="field is-grouped">
                <Button
                  className="black-yellow control"
                  onClick={() => {
                    onSubmit({
                      values: state,
                      auth,
                      url,
                      setErrors,
                      setState,
                      date_fields,
                    });
                  }}
                >
                  Save
                </Button>
                {!isEmpty(state._id) ? (
                  <Button
                    className="control"
                    type="danger"
                    icon={<i className="fas fa-times pad-right-8"></i>}
                    onClick={() => {
                      onDelete({
                        id: state._id,
                        url,
                      });
                      setState(initialValues);
                    }}
                  >
                    Delete
                  </Button>
                ) : null}
                <Button
                  className="control"
                  onClick={() => {
                    onSearch({
                      page: current_page,
                      search_keyword,
                      url,
                      setRecords,
                      setTotalRecords,
                      setCurrentPage,
                      setErrors,
                      advance_search: search_state,
                    });
                  }}
                >
                  Exit
                </Button>
              </div>
            </Form.Item>
          </Form>
        ) : (
          <Table
            dataSource={records}
            columns={records_column}
            rowKey={(record) => record._id}
            onRow={(record, index) => {
              return {
                onDoubleClick: (e) => {
                  edit({
                    record,
                    setState,
                    setErrors,
                    setRecords,
                    url,
                    date_fields,
                  });
                },
              };
            }}
            pagination={{
              current: current_page,
              defaultCurrent: current_page,
              onChange: (page) =>
                onSearch({
                  page,
                  search_keyword,
                  url,
                  setRecords,
                  setTotalRecords,
                  setCurrentPage,
                  setErrors,
                  advance_search: search_state,
                }),
              total: total_records,
              pageSize: 10,
            }}
          />
        )}
      </div>
    </Content>
  );
}
